import type {
  FinancialSnapshotDocument,
  FinancialSnapshotResponse,
  FinancialSnapshotTemplate,
  SnapshotCategoryData,
  SnapshotMetrics,
  SnapshotStatus,
} from '../financial-snapshot.model';
import {
  deriveNetCashflowStatus,
  recomputeSnapshot,
} from '../compute/financial-snapshot-compute';

const SNAPSHOT_STATUSES: readonly SnapshotStatus[] = [
  'empty',
  'in_progress',
  'complete',
  'outdated',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** True when `value` looks like a Financial Snapshot template map. */
export function isFinancialSnapshotTemplate(value: unknown): value is FinancialSnapshotTemplate {
  if (!isRecord(value)) {
    return false;
  }
  const entries = Object.values(value);
  if (entries.length === 0) {
    return false;
  }
  return entries.every(
    (entry) =>
      isRecord(entry) &&
      typeof entry['name'] === 'string' &&
      typeof entry['macroCategory'] === 'string'
  );
}

/** True when `value` is a usable snapshot document (guid + categories). */
export function isFinancialSnapshotPayload(value: unknown): value is FinancialSnapshotDocument {
  if (!isRecord(value)) {
    return false;
  }
  return typeof value['guid'] === 'string' && isRecord(value['categories']);
}

function isSnapshotStatus(value: unknown): value is SnapshotStatus {
  return typeof value === 'string' && (SNAPSHOT_STATUSES as readonly string[]).includes(value);
}

function coerceMetrics(raw: unknown, fallback: SnapshotMetrics): SnapshotMetrics {
  if (!isRecord(raw)) {
    return fallback;
  }
  const householdIncome =
    typeof raw['householdIncome'] === 'number' ? raw['householdIncome'] : fallback.householdIncome;
  const monthlyCosts =
    typeof raw['monthlyCosts'] === 'number' ? raw['monthlyCosts'] : fallback.monthlyCosts;
  const netCashflow =
    typeof raw['netCashflow'] === 'number' ? raw['netCashflow'] : fallback.netCashflow;
  const costsVsIncomePercent =
    typeof raw['costsVsIncomePercent'] === 'number'
      ? raw['costsVsIncomePercent']
      : fallback.costsVsIncomePercent;
  const netCashflowStatus =
    raw['netCashflowStatus'] === 'surplus' ||
    raw['netCashflowStatus'] === 'shortfall' ||
    raw['netCashflowStatus'] === 'break_even'
      ? raw['netCashflowStatus']
      : deriveNetCashflowStatus(netCashflow);

  return {
    householdIncome,
    monthlyCosts,
    netCashflow,
    netCashflowStatus,
    costsVsIncomePercent,
  };
}

function mergeCategoriesPreferServerPercent(
  server: Record<string, SnapshotCategoryData>,
  recomputed: Record<string, SnapshotCategoryData>
): Record<string, SnapshotCategoryData> {
  const out: Record<string, SnapshotCategoryData> = { ...server };
  for (const [key, data] of Object.entries(server)) {
    const re = recomputed[key];
    if (data.percentOfIncome === undefined || data.percentOfIncome === null) {
      if (re?.percentOfIncome !== undefined) {
        out[key] = { ...data, percentOfIncome: re.percentOfIncome };
      }
    } else {
      out[key] = data;
    }
  }
  return out;
}

/**
 * Normalize a live GET payload into the UI read model.
 * Prefers server `status` / `metrics` / `percentOfIncome`; derives `netCashflowStatus`
 * when missing; falls back to full recompute when status/metrics are absent.
 */
export function normalizeFinancialSnapshotResponse(
  payload: FinancialSnapshotDocument,
  template: FinancialSnapshotTemplate
): FinancialSnapshotResponse {
  const hasStatus = isSnapshotStatus(payload.status);
  const hasMetrics = isRecord(payload.metrics);

  if (!hasStatus || !hasMetrics) {
    return recomputeSnapshot(payload, template);
  }

  const recomputed = recomputeSnapshot(payload, template);
  const metrics = coerceMetrics(payload.metrics, recomputed.metrics);
  const categories = mergeCategoriesPreferServerPercent(payload.categories, recomputed.categories);

  return {
    guid: payload.guid,
    createdDate: payload.createdDate,
    modifiedDate: payload.modifiedDate,
    submittedDate: payload.submittedDate,
    lastUpdatedAt: payload.lastUpdatedAt,
    status: payload.status as SnapshotStatus,
    categories,
    metrics,
  };
}
